/**
 * Formula helpers for CBP calculated columns.
 *
 * A calculated column's formula references other columns by id in square brackets — `[columnId]` —
 * the same convention the TPA/calculation formula editors use (see ATPTextAreaAutoComplete, which
 * emits `[value]` on `@`-insert). Aggregates wrap a reference, e.g. `SUM([headcount])`.
 */
import { ExpressionPart, ExpressionPartTypes } from 'src/models/calculations/CalculationDetailsResult';

const REFERENCE = /\[([^\]]+)\]/g;

/**
 * Extract the distinct `[columnId]` references a formula reads — the columns it depends on, including
 * those inside aggregates like `SUM([x])`. Used for the persisted `dependsOn` (advisory: the server
 * re-derives dependencies from the formula text) and for the builder's own self-reference /
 * unknown-reference validation. On the wire `dependsOn` is simply every column the formula reads;
 * there is no separate aggregate-dependency field, so none is computed here.
 */
export function extractFormulaReferences(formula = ''): string[] {
    const refs = new Set<string>();
    const re = new RegExp(REFERENCE);
    let match: RegExpExecArray | null;
    while ((match = re.exec(formula)) !== null) refs.add(match[1]);
    return Array.from(refs);
}

/**
 * Map a `[columnId]` formula to ExpressionParts for the shared `renderExpressionParts` renderer —
 * references become STEP parts (rendered by the existing `.calcExpression-S` outlined pill style),
 * everything else is an OPERATOR part (plain text).
 */
export function toExpressionParts(formula = '', nameById: Record<string, string>): ExpressionPart[] {
    const parts: ExpressionPart[] = [];
    const re = new RegExp(REFERENCE);
    let last = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(formula)) !== null) {
        if (match.index > last) parts.push({ type: ExpressionPartTypes.OPERATOR, value: formula.slice(last, match.index) });
        const id = match[1];
        parts.push({ type: ExpressionPartTypes.STEP, value: id, displayValue: nameById[id] ?? id });
        last = match.index + match[0].length;
    }
    if (last < formula.length) parts.push({ type: ExpressionPartTypes.OPERATOR, value: formula.slice(last) });
    return parts;
}

/**
 * Builds the two lookups the formula editor uses to show `[name]` while the stored formula keeps
 * `[columnId]`. A name is mapped only when it identifies exactly one column (trimmed, case-insensitive)
 * and holds no square bracket; any other column keeps its raw id in the editor, so converting the
 * text back can never silently retarget a reference to a different column.
 */
export function buildFormulaNameMaps(columns: Array<{ columnId: string; displayName?: string }>): {
    nameById: Record<string, string>;
    idByName: Record<string, string>;
    columnIds: Set<string>;
} {
    const counts: Record<string, number> = {};
    columns.forEach((c) => {
        const key = (c.displayName || '').trim().toLowerCase();
        if (key) counts[key] = (counts[key] ?? 0) + 1;
    });
    const nameById: Record<string, string> = {};
    const idByName: Record<string, string> = {};
    columns.forEach((c) => {
        const name = (c.displayName || '').trim();
        const key = name.toLowerCase();
        if (!name || counts[key] !== 1 || /[[\]]/.test(name)) return;
        nameById[c.columnId] = name;
        idByName[key] = c.columnId;
    });
    return { nameById, idByName, columnIds: new Set(columns.map((c) => c.columnId)) };
}

/** Stored `[columnId]` formula → the `[name]` text shown in the editor. Unmapped ids are left as-is. */
export function formulaIdsToNames(formula = '', nameById: Record<string, string>): string {
    return formula.replace(new RegExp(REFERENCE), (match, id: string) => (nameById[id] != null ? `[${nameById[id]}]` : match));
}

/**
 * Editor `[name]` text → the stored `[columnId]` formula. A reference that names no column (a typo, or
 * a raw id) is left as-is, so Save's unknown-reference check reports it by the text the user sees. A
 * reference that is exactly an existing column id (one the editor kept because its name is ambiguous)
 * is never looked up by name, so a column whose name happens to match that id can't capture it.
 */
export function formulaNamesToIds(
    formula = '',
    idByName: Record<string, string>,
    columnIds: Set<string> = new Set(),
): string {
    return formula.replace(new RegExp(REFERENCE), (match, ref: string) => {
        if (columnIds.has(ref)) return match;
        const id = idByName[ref.trim().toLowerCase()];
        return id != null ? `[${id}]` : match;
    });
}
